import type { AnimationPlayer } from "./animationPlayer";
import type { BoneColliders } from "./boneColliders";
import type { Clock } from "./clock";
import type { PhysicsWorld } from "./physicsWorld";
import type { PoseInteract } from "./poseInteract";
import type { DriveMode } from "./types";

/**
 * Bridge between animation / pose / ragdoll drivers.
 * H1: pose mode freezes AnimationPlayer bone writes and enables PoseInteract.
 * H2: ragdoll via Rapier bone colliders + fixed-step physics world.
 * H3: global drive mode applies to all humans (per-human driveModeByHuman later).
 *     On low tier, only first maxDynamic human is dynamic; others kinematic (H7).
 */
export class PoseDriver {
  mode: DriveMode = "animation";
  /** When true, next play after leaving pose snaps bones back to the clip. */
  resetPoseOnPlay = true;

  private interact: PoseInteract | null = null;
  private poseDirty = false;
  private physics: PhysicsWorld | null = null;
  private colliders: BoneColliders | null = null;
  /** Physics runs while ragdoll owns bones, or kinematic while anim/pose with bodies. */
  private physicsReady = false;

  setInteract(interact: PoseInteract | null): void {
    this.interact = interact;
  }

  setPhysics(physics: PhysicsWorld | null, colliders: BoneColliders | null): void {
    this.physics = physics;
    this.colliders = colliders;
    this.physicsReady = !!(physics && colliders);
    this.syncColliderMode(this.mode);
  }

  setResetPoseOnPlay(value: boolean): void {
    this.resetPoseOnPlay = value;
  }

  setDriveMode(mode: DriveMode): void {
    const prev = this.mode;
    this.mode = mode;

    if (mode === "pose") {
      this.interact?.setEnabled(true);
      // Rest for joint limits = current (animation or prior) pose at entry.
      this.interact?.captureRestFromCurrent();
      this.poseDirty = true;
    } else {
      this.interact?.setEnabled(false);
      if (prev === "pose" && mode === "animation") {
        // Keep posed bones until play/seek when resetPoseOnPlay is true.
      }
    }

    if (mode === "ragdoll") {
      this.poseDirty = true;
      this.interact?.setEnabled(false);
    }

    this.syncColliderMode(mode);
  }

  private syncColliderMode(mode: DriveMode): void {
    if (!this.colliders) return;
    if (mode === "ragdoll") {
      this.colliders.setMode("dynamic");
    } else {
      // animation + pose: kinematic follow (pose freezes anim but bodies track bones)
      this.colliders.setMode("kinematic");
    }
  }

  /** True while pose/ragdoll actively own bone transforms. */
  ownsBones(): boolean {
    return this.mode === "pose" || this.mode === "ragdoll";
  }

  isPoseDirty(): boolean {
    return this.poseDirty;
  }

  /**
   * Whether AnimationPlayer may write bones this frame.
   * Holds a posed skeleton after leaving pose until Play/Seek (while paused).
   */
  shouldApplyAnimation(playing: boolean): boolean {
    if (this.mode === "pose" || this.mode === "ragdoll") return false;
    // Critical: paused + dirty pose must not snapToClock every frame.
    if (this.poseDirty && !playing) return false;
    return true;
  }

  /**
   * Per-frame physics: kinematic sync before step (anim/pose + non-dynamic ragdoll),
   * dynamic write-back after. Shared world: kinematic sets push / receive contacts.
   */
  updatePhysics(dt: number): void {
    if (!this.physicsReady || !this.physics || !this.colliders) return;
    if (this.colliders.getEntries().length === 0) return;

    // Always push kinematic (or non-budget) bodies from bones before the step.
    this.colliders.syncFromBones(false);
    const steps = this.physics.step(dt);
    if (this.mode === "ragdoll" && steps > 0) {
      this.colliders.applyToBones();
    }
  }

  /**
   * Call from play(): optionally restore clip pose after interactive posing.
   * Caller must already be in (or switch to) animation mode.
   * Returns true if a snap was applied.
   */
  onPlay(anim: AnimationPlayer, clock: Clock): boolean {
    if (this.mode !== "animation") {
      return false;
    }
    if (this.resetPoseOnPlay && this.poseDirty) {
      anim.snapToClock(clock);
      this.poseDirty = false;
      this.colliders?.syncFromBones(true);
      return true;
    }
    // Playing without reset: still clear dirty so mixer can drive again.
    if (this.poseDirty && !this.resetPoseOnPlay) {
      this.poseDirty = false;
    }
    return false;
  }

  /** Seek in animation mode clears pose dirt when we overwrite bones. */
  onSeek(anim: AnimationPlayer, clock: Clock): void {
    if (this.mode === "animation") {
      anim.snapToClock(clock);
      this.poseDirty = false;
      this.colliders?.syncFromBones(true);
    }
  }

  markPoseDirty(): void {
    this.poseDirty = true;
  }
}
